import { Link, useLocation } from 'react-router'
import { loginHref } from '../lib/analytics'
import { MainNav } from './MainNav'
import { SearchBox } from './SearchBox'
import { BellIcon, SearchIcon } from './icons'
import styles from './AppHeader.module.css'

export interface HeaderUser { email: string; displayName?: string | null; isAdmin?: boolean }

export interface AppHeaderProps {
  /** `undefined` while the session loads, `null` when signed out. */
  user: HeaderUser | null | undefined
  /** Unread alert count; `null` hides the dot (not connected yet). */
  unread?: number | null
  environment?: string
  /** Accessible name of the nav landmark (unique per page). */
  navLabel?: string
}

export function AppHeader({ user, unread = null, environment, navLabel = 'Main' }: AppHeaderProps) {
  const location = useLocation()
  const initial = (user?.displayName || user?.email || '?').charAt(0).toUpperCase()
  return (
    <header className={styles.header}>
      <Link to="/" className={styles.brand} aria-label="BCEIE home">
        <span className={styles.mark} aria-hidden="true">B</span>
        <span className={styles.wordmark}>
          <span className={styles.name}>BCEIE</span>
          <span className={styles.tagline}>Markets &amp; corporate intelligence</span>
        </span>
      </Link>
      {environment && <span className={styles.env}>{environment}</span>}
      <MainNav label={navLabel} />
      <div role="search" aria-label={navLabel === 'Main' ? 'Site search' : `${navLabel} search`} className={styles.searchSlot}>
        <SearchBox enabled={!!user} />
      </div>
      <Link to="/search" className={`${styles.iconBtn} ${styles.searchBtn}`} aria-label="Search">
        <SearchIcon size={18} />
      </Link>
      <Link
        to="/alerts"
        className={styles.iconBtn}
        aria-label={unread ? `Alerts, ${unread} unread` : 'Alerts'}
      >
        <BellIcon size={18} />
        {unread ? <span className={styles.dot} aria-hidden="true" /> : null}
      </Link>
      {user?.isAdmin && (
        <span className={styles.adminLinks}>
          <Link to="/admin/live" className={styles.adminBtn}><span className={styles.liveDot} aria-hidden="true" />Live rates</Link>
          <Link to="/admin" className={styles.adminBtn}>Admin</Link>
        </span>
      )}
      {user === undefined ? (
        <span className={`${styles.avatar} ${styles.avatarLoading}`} aria-hidden="true" />
      ) : user ? (
        <Link to="/account" className={styles.avatar} aria-label={`Account: ${user.email}`} title={user.email}>{initial}</Link>
      ) : (
        <Link to={loginHref(location.pathname + location.search)} className={styles.signIn}>Sign in</Link>
      )}
    </header>
  )
}
